<template>
  <article v-if="!error" class="flex flex-col space-y-4 lg:space-y-6">
    <!-- Page header -->
    <OrganizationProfileHeader :loading="pending" :org="org">
      <template #tabs>
        <Tabs v-model="current" :tabs="paths" />
      </template>
    </OrganizationProfileHeader>
    <PageContainer>
      <NamiLoading v-if="pending" />
      <NuxtPage
        v-else-if="org"
        :orgName="org.attributes.name"
        :org="org"
        keepalive
      />
    </PageContainer>
  </article>
  <ErrorPage v-else :error />
</template>

<script setup lang="ts">
import { ApiToNuxtError } from "~/utils/errors/404";
import PageContainer from "~/components/PageContainer.vue";
import { useQuery } from "@tanstack/vue-query";
import { useOrganizationGetBySlug } from "~/utils/tanstack/organization";

const route = useRoute();
const nuxtApp = useNuxtApp();
const translate = nuxtApp.$i18n.global.t;
const config = useRuntimeConfig();
const { getOrganizationAvatar } = useMediaLink();

const locale = nuxtApp.$i18n.global.locale;

const orgSlug = computed(() => route.params.slug as string);

const {
  data: org,
  error,
  isPending: pending,
  suspense,
} = useQuery(useOrganizationGetBySlug(orgSlug));

onServerPrefetch(async () => {
  await suspense();

  if (!org.value) return;

  const seo = {
    name: org.value.attributes.name,
    description: `Support ${org.value.attributes.name} on NamiComi!`,
    slug: org.value.attributes.slug,
    imgUrl: getOrganizationAvatar(org.value, "512"),
    href: `${config.public.baseUrl}${linkTo(org.value)}`,
  };

  nuxtApp.runWithContext(() =>
    useServerSeoMeta({
      title: seo.name,
      ogTitle: seo.name,
      twitterTitle: seo.name,
      description: seo.description,
      ogDescription: seo.description,
      twitterDescription: seo.description,
      ogUrl: seo.href,
      ogLocale: locale.value,
      ogImage: seo.imgUrl,
      ogImageSecureUrl: seo.imgUrl,
      twitterImage: seo.imgUrl,
      ogImageHeight: "1200",
      ogImageWidth: "630",
      ogImageUrl: seo.imgUrl,
      ogImageType: "image/png",
      ogImageAlt: "NamiComi",
      ogLocaleAlternate: "en_GB",
    }),
  );
});

use404Response(ApiToNuxtError(error));

const { current, paths } = useSubpaths(
  computed(() => ({
    default: "",
    rootPath: linkTo("org", orgSlug.value),
    paths: [
      { name: translate("site.org.id.slug.tabPosts"), value: "" },
      { name: translate("site.org.id.slug.tabTitles"), value: "titles" },
      { name: translate("site.org.id.slug.tabFollowers"), value: "followers" },
      {
        name: translate("site.org.id.slug.tabSubscriptions"),
        value: "subscriptions",
      },
      {
        name: translate("site.org.id.slug.tabRewards"),
        value: "rewards",
      },
      {
        name: translate("site.org.id.slug.tab.achievements"),
        value: "achievements",
      },
    ],
  })),
);
</script>
